import { useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useQuery } from '@tanstack/react-query'
import { Check, Clock, Loader2, Play } from 'lucide-react'
import { Trans, useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'

import {
  Sheet,
  SheetContent,
  SheetTitle,
} from '@/components/ui/sheet'
import { track } from '@/lib/analytics'
import { formatDateTime, formatRelative } from '@/lib/dates'
import { formatMoney } from '@/lib/format'

// ─────────────────────────────────────────────────────────────────────
// Types

type CallStatus =
  | 'success'
  | 'in_progress'
  | 'on_hold'
  | 'voicemail'
  | 'vin_mismatch'
  | 'refused_ai'
  | 'failed'
  | (string & {})

type Confidence = 'high' | 'medium' | 'low' | null

type TranscriptLine = {
  t: string
  speaker: string
  text: string
}

type Alternative = {
  brand?: string
  part_number?: string
  price?: number
  lead_time?: string
}

type Call = {
  id: string
  status: CallStatus
  dealer_name: string | null
  dealer_brand: string | null
  customer_id: string | null
  vehicle_id: string | null
  vehicle_text: string | null
  part_description: string | null
  part_number: string | null
  vin: string | null
  price: number | null
  qty: number | null
  lead_time: string | null
  in_stock: boolean | null
  notes: string | null
  confidence: Confidence
  duration: string | null
  elapsed: string | null
  alternatives: Alternative[] | null
  transcript: TranscriptLine[] | null
  when_relative: string | null
  when_full: string | null
  started_at: string | null
  ended_at: string | null
}

// ─────────────────────────────────────────────────────────────────────
// Status chip styling

// Visual styling for the status chip — chip color + optional icon. The
// chip's display label is looked up at render time via `t('common:status.<key>')`
// so it translates with the rest of the UI.
type StatusStyle = {
  chip: string
  Icon?: typeof Check
  iconClassName?: string
  // i18next key (under the `common.status` namespace) for the chip's display text.
  labelKey: string
}

// §4.9 status chip, neutral variant (surface / ink-2 / line). Call
// outcomes are FACTS (§2): nothing here is "saved and undoable" (green)
// and none of these statuses is a decision waiting on the mechanic
// (amber) — that surface is a needs-you card, not this log — so every
// call status renders neutral. Label + icon carry the meaning.
const NEUTRAL_CHIP = 'border border-border bg-muted text-muted-foreground'

const STATUS_STYLES: Record<string, StatusStyle> = {
  success: {
    labelKey: 'status.got_quote',
    chip: NEUTRAL_CHIP,
    Icon: Check,
  },
  in_progress: {
    labelKey: 'status.calling',
    chip: NEUTRAL_CHIP,
    Icon: Loader2,
    iconClassName: 'animate-spin motion-reduce:animate-none',
  },
  on_hold: {
    labelKey: 'status.on_hold',
    chip: NEUTRAL_CHIP,
    Icon: Clock,
  },
  voicemail: {
    labelKey: 'status.voicemail',
    chip: NEUTRAL_CHIP,
  },
  vin_mismatch: {
    labelKey: 'status.vin_mismatch',
    chip: NEUTRAL_CHIP,
  },
  refused_ai: {
    labelKey: 'status.refused_ai',
    chip: NEUTRAL_CHIP,
  },
}

function statusStyle(status: string): StatusStyle | null {
  return STATUS_STYLES[status] ?? null
}

function StatusChip({ status }: { status: string }) {
  const { t } = useTranslation('common')
  const s = statusStyle(status)
  // For unknown statuses, render the raw value with neutral styling so
  // the chip degrades gracefully.
  const chip = s?.chip ?? NEUTRAL_CHIP
  const Icon = s?.Icon
  // i18next returns the key string when missing; supply `defaultValue`
  // so unknown statuses don't show "status.foo" in the UI.
  const label = s ? t(s.labelKey) : status
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-[9px] py-[3px] text-xs font-[650] ${chip}`}
    >
      {Icon ? <Icon className={`h-3 w-3 ${s?.iconClassName ?? ''}`.trim()} /> : null}
      <span>{label}</span>
    </span>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Filter chips

type CallFilter = 'all' | 'success' | 'in_progress' | 'needs_attention'
const FILTERS: CallFilter[] = ['all', 'success', 'in_progress', 'needs_attention']

const NEEDS_ATTENTION_STATUSES = new Set([
  'vin_mismatch',
  'voicemail',
  'refused_ai',
  'failed',
])

function applyFilter(calls: Call[], filter: CallFilter): Call[] {
  if (filter === 'all') return calls
  if (filter === 'success') return calls.filter((c) => c.status === 'success')
  if (filter === 'in_progress')
    return calls.filter((c) => c.status === 'in_progress' || c.status === 'on_hold')
  return calls.filter((c) => NEEDS_ATTENTION_STATUSES.has(c.status as string))
}

function filterLabel(t: TFunction<'calls'>, f: CallFilter): string {
  // Filter labels humanize the underscored enum value via the
  // calls:filter.* keys.
  return t(`filter.${f}`)
}

// ─────────────────────────────────────────────────────────────────────
// Confidence chip

function confidenceChipClass(_c: Confidence): string {
  // §2 lane semantics: extraction confidence is a data-quality FACT —
  // not a save (green), not a pending decision (amber) — so every level
  // renders the §4.9 neutral variant; the label carries the level.
  return NEUTRAL_CHIP
}

function confidenceLabel(t: TFunction<'common'>, c: Confidence): string {
  return c ? t(`confidence.${c}`) : t('confidence.pending')
}

// ─────────────────────────────────────────────────────────────────────
// Page

export function Calls() {
  const { t } = useTranslation('calls')
  const { t: tCommon } = useTranslation('common')
  const { getToken, isLoaded } = useAuth()
  const [filter, setFilter] = useState<CallFilter>('all')
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const callsQuery = useQuery({
    queryKey: ['calls'],
    enabled: isLoaded,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch('/api/calls', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      return (await res.json()) as Call[]
    },
  })

  const filtered = useMemo(
    () => applyFilter(callsQuery.data ?? [], filter),
    [callsQuery.data, filter],
  )

  const selectedCall =
    selectedId !== null
      ? (callsQuery.data ?? []).find((c) => c.id === selectedId) ?? null
      : null

  const open = selectedCall !== null

  // call.list_viewed — fires once per query success. Dedupes on data
  // identity (TanStack returns the same array ref until refetch), so
  // re-renders with cached data are no-ops.
  const trackedDataRef = useRef<Call[] | null>(null)
  useEffect(() => {
    if (!callsQuery.data) return
    if (trackedDataRef.current === callsQuery.data) return
    trackedDataRef.current = callsQuery.data
    const all = callsQuery.data
    track('call.list_viewed', {
      total_count: all.length,
      success_count: all.filter((c) => c.status === 'success').length,
      in_progress_count: all.filter(
        (c) => c.status === 'in_progress' || c.status === 'on_hold',
      ).length,
      needs_attention_count: all.filter((c) =>
        NEEDS_ATTENTION_STATUSES.has(c.status as string),
      ).length,
    })
  }, [callsQuery.data])

  // call.filter_changed — fires on actual user-driven filter change,
  // not on the initial 'all' render. `result_count` is the post-filter
  // length so we can see if a filter ever yields zero (UX deadend).
  const onFilterChange = (next: CallFilter) => {
    if (next === filter) return
    const all = callsQuery.data ?? []
    const result = applyFilter(all, next)
    track('call.filter_changed', { filter: next, result_count: result.length })
    setFilter(next)
  }

  // call.detail_opened — fires on every row/card click that opens the
  // drawer. Wrapping setSelectedId so all 4 click handlers below pick
  // this up without duplication.
  const openDetail = (id: string) => {
    const call = (callsQuery.data ?? []).find((c) => c.id === id)
    if (call) {
      track('call.detail_opened', {
        call_id: call.id,
        status: call.status,
        has_transcript: (call.transcript?.length ?? 0) > 0,
        confidence: call.confidence,
      })
    }
    setSelectedId(id)
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-[22px] font-bold tracking-[-0.015em] text-foreground [text-wrap:balance]">
            {t('header.title')}
          </h1>
          <p className="mt-0.5 text-[13px] text-muted-foreground">
            {t('header.subtitle', { count: callsQuery.data?.length ?? 0 })}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {FILTERS.map((f) => {
            const active = filter === f
            return (
              <button
                key={f}
                type="button"
                onClick={() => onFilterChange(f)}
                className={
                  // §4.3 chip shape (44px pill, 14px/600); filters are human
                  // navigation → ink lane (§2), so active = ink fill, never
                  // saved green. Press = §7 scale 0.97; duration rides the
                  // 150ms default — duration-[120ms] is a repo-wide silent
                  // no-op (tailwindcss-animate's `duration` collides with
                  // core, so the arbitrary value never generates).
                  'min-h-11 rounded-full border px-4 text-[14px] font-semibold capitalize transition-[background-color,border-color,color,transform] active:scale-[0.97] ' +
                  (active
                    ? 'border-foreground bg-foreground text-background'
                    : 'border-border bg-background text-muted-foreground [@media(hover:hover)_and_(pointer:fine)]:hover:bg-muted')
                }
              >
                {filterLabel(t, f)}
              </button>
            )
          })}
        </div>
      </div>

      {callsQuery.isError ? (
        <div className="rounded-[14px] border border-destructive/25 bg-destructive-soft p-4 text-sm text-destructive">
          {callsQuery.error instanceof Error
            ? callsQuery.error.message
            : t('error')}
        </div>
      ) : null}

      {filtered.length === 0 && !callsQuery.isLoading ? (
        <div className="rounded-[14px] border border-dashed border-border bg-background p-12 text-center text-sm text-muted-foreground">
          {t('empty')}
        </div>
      ) : (
        <>
          {/* Mobile / tablet — §4.8 list rows in one bordered container
              (below lg): hairline separators, no zebra, no side-stripes;
              press = surface flash + scale per the More.tsx row idiom. */}
          <div className="overflow-hidden rounded-[14px] border border-border bg-background lg:hidden">
            {filtered.map((call) => (
              <button
                key={call.id}
                type="button"
                onClick={() => openDetail(call.id)}
                className="block min-h-[60px] w-full border-t border-border bg-background px-3.5 py-3 text-left transition-[background-color,transform] first:border-t-0 active:scale-[0.99] active:bg-muted [@media(hover:hover)_and_(pointer:fine)]:hover:bg-muted"
              >
                <div className="flex items-start justify-between gap-3">
                  <StatusChip status={call.status} />
                  <span className="text-[12px] text-ink-3">
                    {call.started_at
                      ? formatRelative(call.started_at)
                      : call.when_relative ?? ''}
                  </span>
                </div>
                <div className="mt-2 text-[15px] font-[620] text-foreground">
                  {call.dealer_name ?? '—'}
                </div>
                {call.dealer_brand ? (
                  <div className="text-[13px] text-muted-foreground">{call.dealer_brand}</div>
                ) : null}
                {call.vehicle_text ? (
                  <div className="mt-2 text-[13px] text-muted-foreground">{call.vehicle_text}</div>
                ) : null}
                <div className="mt-2 text-sm text-foreground">
                  {call.part_description ?? '—'}
                </div>
                {call.part_number ? (
                  <div className="font-mono text-xs tabular-nums text-muted-foreground">{call.part_number}</div>
                ) : null}
                <div className="mt-3 flex items-center justify-between gap-3 text-xs">
                  <div
                    className={
                      call.price !== null
                        ? 'font-mono font-semibold tabular-nums text-foreground'
                        : 'font-mono tabular-nums text-ink-3'
                    }
                  >
                    {call.price !== null ? formatMoney(call.price) : '—'}
                  </div>
                  <div className="text-muted-foreground">{call.lead_time ?? '—'}</div>
                </div>
              </button>
            ))}
          </div>

          {/* Desktop — full table (lg+) */}
          <div className="hidden lg:block overflow-hidden rounded-[14px] border border-border bg-background">
            <table className="w-full text-sm">
              {/* §3.1: no uppercase tracked labels — column heads are plain
                  12px ink-2 on a quiet surface fill. */}
              <thead className="border-b border-border bg-muted text-xs text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">{t('table.status')}</th>
                  <th className="px-4 py-3 text-left font-medium">{t('table.dealer')}</th>
                  <th className="px-4 py-3 text-left font-medium">{t('table.vehicle')}</th>
                  <th className="px-4 py-3 text-left font-medium">{t('table.part')}</th>
                  <th className="px-4 py-3 text-right font-medium">{t('table.price')}</th>
                  <th className="px-4 py-3 text-left font-medium">{t('table.lead')}</th>
                  <th className="px-4 py-3 text-right font-medium">{t('table.when')}</th>
                  <th className="px-2 py-3" aria-hidden="true" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map((call) => (
                  <tr
                    key={call.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => openDetail(call.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        openDetail(call.id)
                      }
                    }}
                    className="cursor-pointer transition-[background-color] active:bg-muted focus:outline-none focus-visible:bg-muted [@media(hover:hover)_and_(pointer:fine)]:hover:bg-muted"
                  >
                    <td className="px-4 py-3">
                      <StatusChip status={call.status} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-[620] text-foreground">
                        {call.dealer_name ?? '—'}
                      </div>
                      {call.dealer_brand ? (
                        <div className="text-xs text-muted-foreground">{call.dealer_brand}</div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                      {call.vehicle_text ?? '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-foreground">
                        {call.part_description ?? '—'}
                      </div>
                      {call.part_number ? (
                        <div className="font-mono text-xs tabular-nums text-muted-foreground">
                          {call.part_number}
                        </div>
                      ) : null}
                    </td>
                    <td
                      className={
                        'px-4 py-3 text-right font-mono tabular-nums ' +
                        (call.price !== null
                          ? 'font-semibold text-foreground'
                          : 'text-ink-3')
                      }
                    >
                      {call.price !== null ? formatMoney(call.price) : '—'}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {call.lead_time ?? '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-xs text-ink-3">
                      {call.started_at
                        ? formatRelative(call.started_at)
                        : call.when_relative ?? ''}
                    </td>
                    <td className="px-2 py-3 text-ink-3" aria-hidden="true">
                      ›
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Detail drawer — controlled by selectedId */}
      <Sheet
        open={open}
        onOpenChange={(next) => {
          if (!next) setSelectedId(null)
        }}
      >
        <SheetContent
          side="right"
          className="w-full gap-0 overflow-y-auto p-0 sm:max-w-xl"
        >
          {/* `key` resets the local body state (transcript expand) when the
              user opens a different call without closing the drawer. */}
          {selectedCall ? (
            <CallDrawerBody key={selectedCall.id} call={selectedCall} />
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Drawer body

function CallDrawerBody({ call }: { call: Call }) {
  const { t } = useTranslation('calls')
  const [showFullTranscript, setShowFullTranscript] = useState(false)

  // The Sheet primitive's built-in close button is positioned absolute
  // top-right; reserve space with pr-12 in the sticky header so the
  // status chip + timestamp don't slide under it.
  return (
    <div className="flex h-full flex-col">
      <div className="sticky top-0 z-10 border-b border-border bg-background px-6 py-5 pr-12">
        <div className="mb-1.5 flex flex-wrap items-center gap-2">
          <StatusChip status={call.status} />
          {call.started_at || call.when_full ? (
            <span className="text-[12px] text-ink-3">
              {call.started_at
                ? formatDateTime(call.started_at)
                : call.when_full}
            </span>
          ) : null}
        </div>
        {/* Radix's Dialog.Title is required for screen-reader announcement
            on open. Make it the dealer name so the announcement reads as
            "<dealer name> dialog" rather than something awkward like the
            status chip text. */}
        <SheetTitle className="text-lg font-semibold text-foreground">
          {call.dealer_name ?? t('drawer.no_result')}
        </SheetTitle>
        {(call.vehicle_text || call.part_description) ? (
          <p className="text-[13px] text-muted-foreground">
            {[call.vehicle_text, call.part_description].filter(Boolean).join(' · ')}
          </p>
        ) : null}
      </div>

      <div className="border-b border-border px-6 py-5">
        <SectionLabel>{t('drawer.structured_result')}</SectionLabel>
        <StructuredResult call={call} />
        <ConfidenceRow confidence={call.confidence} />
      </div>

      <div className="border-b border-border px-6 py-5">
        <Transcript
          lines={call.transcript ?? []}
          expanded={showFullTranscript}
          onToggle={() => setShowFullTranscript((v) => !v)}
        />
      </div>

      <div className="px-6 py-5">
        <RecordingShell duration={call.duration} />
      </div>
    </div>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  // §3.1 section labels: 13px/600 ink-2 — uppercase tracked eyebrows
  // are banned.
  return (
    <div className="mb-3 text-[13px] font-semibold text-muted-foreground">
      {children}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Status-dependent body

function StructuredResult({ call }: { call: Call }) {
  const { t } = useTranslation('calls')

  // §2 lane semantics: these boxes state facts about the call — no lane
  // color. Amber would claim "waiting on you", red would claim
  // destructive; neither is true of a status notice.
  if (call.status === 'in_progress' || call.status === 'on_hold') {
    return (
      <div className="rounded-[10px] border border-border bg-muted p-4 text-sm text-foreground">
        {/* The trailing "navigating the IVR." / "on hold." is bold inside
            the sentence — handled with Trans + a `<strong>` component. */}
        <Trans
          ns="calls"
          i18nKey={
            call.status === 'on_hold'
              ? 'drawer.on_hold'
              : 'drawer.in_progress'
          }
          values={{ elapsed: call.elapsed ?? '—' }}
          components={{ strong: <span className="font-semibold" /> }}
        />
      </div>
    )
  }

  if (call.status === 'voicemail') {
    return (
      <div className="rounded-[10px] border border-border bg-muted p-4 text-sm text-foreground">
        {t('drawer.voicemail')}
      </div>
    )
  }

  if (call.status === 'vin_mismatch') {
    return (
      <div className="rounded-[10px] border border-border bg-muted p-4 text-sm text-foreground">
        {t('drawer.vin_mismatch')}
        {call.notes ? (
          <div className="mt-2 break-words font-mono text-xs text-muted-foreground">
            “{call.notes}”
          </div>
        ) : null}
      </div>
    )
  }

  if (call.status === 'refused_ai') {
    return (
      <div className="rounded-[10px] border border-border bg-muted p-4 text-sm text-foreground">
        {t('drawer.refused_ai')}
        {call.notes ? (
          <div className="mt-2 break-words font-mono text-xs text-muted-foreground">
            “{call.notes}”
          </div>
        ) : null}
      </div>
    )
  }

  if (call.status === 'success') {
    return (
      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
        <div>
          <dt className="text-xs text-muted-foreground">
            {t('drawer.success.in_stock_label')}
          </dt>
          {/* §2: stock state is a FACT — green would claim
              "saved-and-undoable"; the words carry it. */}
          <dd className="font-semibold text-foreground">
            {call.in_stock
              ? t('drawer.success.in_stock_yes')
              : t('drawer.success.in_stock_no')}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">
            {t('drawer.success.quantity_label')}
          </dt>
          <dd className="font-semibold text-foreground">
            {call.qty !== null ? call.qty : '—'}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">
            {t('drawer.success.price_label')}
          </dt>
          <dd className="font-mono font-semibold tabular-nums text-foreground">
            {call.price !== null ? formatMoney(call.price) : '—'}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">
            {t('drawer.success.lead_time_label')}
          </dt>
          <dd className="font-semibold text-foreground">{call.lead_time ?? '—'}</dd>
        </div>
        <div className="col-span-2">
          <dt className="text-xs text-muted-foreground">
            {t('drawer.success.notes_label')}
          </dt>
          <dd className="text-foreground">{call.notes ?? '—'}</dd>
        </div>
        {call.alternatives && call.alternatives.length > 0 ? (
          <div className="col-span-2 mt-2">
            <dt className="mb-1 text-xs text-muted-foreground">
              {t('drawer.success.alternative_label')}
            </dt>
            <div className="space-y-2">
              {call.alternatives.map((alt, i) => (
                <dd
                  key={`${alt.brand ?? 'alt'}-${i}`}
                  className="flex items-center justify-between gap-3 rounded-[10px] border border-border bg-muted p-3 text-sm"
                >
                  <div className="min-w-0">
                    <div className="font-medium text-foreground">
                      {[alt.brand, alt.part_number].filter(Boolean).join(' · ')}
                    </div>
                    {alt.lead_time ? (
                      <div className="text-xs text-muted-foreground">{alt.lead_time}</div>
                    ) : null}
                  </div>
                  <div className="shrink-0 font-mono font-semibold tabular-nums text-foreground">
                    {alt.price !== undefined ? formatMoney(alt.price) : ''}
                  </div>
                </dd>
              ))}
            </div>
          </div>
        ) : null}
      </dl>
    )
  }

  // Unknown / failed / other — keep something visible so the drawer
  // doesn't render an empty Structured Result section.
  return (
    <div className="rounded-[10px] border border-border bg-muted p-4 text-sm text-foreground">
      {t('drawer.no_result')}
      {call.notes ? (
        <div className="mt-2 break-words font-mono text-xs text-muted-foreground">
          “{call.notes}”
        </div>
      ) : null}
    </div>
  )
}

function ConfidenceRow({ confidence }: { confidence: Confidence }) {
  const { t } = useTranslation('common')
  return (
    <div className="mt-4 flex items-center gap-2 text-xs">
      <span className="text-muted-foreground">{t('confidence.label')}</span>
      <span
        className={`inline-flex items-center rounded-full px-[9px] py-[3px] font-[650] capitalize ${confidenceChipClass(confidence)}`}
      >
        {confidenceLabel(t, confidence)}
      </span>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Transcript

function Transcript({
  lines,
  expanded,
  onToggle,
}: {
  lines: TranscriptLine[]
  expanded: boolean
  onToggle: () => void
}) {
  const { t } = useTranslation('calls')
  const total = lines.length
  const visible = expanded ? lines : lines.slice(0, 5)
  const hidden = total - visible.length

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <SectionLabel>{t('transcript.header')}</SectionLabel>
        {total > 5 ? (
          <button
            type="button"
            onClick={onToggle}
            className="flex min-h-11 items-center text-[13px] font-semibold text-muted-foreground transition-[color,opacity] active:opacity-70 [@media(hover:hover)_and_(pointer:fine)]:hover:text-foreground"
          >
            {expanded ? t('transcript.collapse') : t('transcript.expand')}
          </button>
        ) : null}
      </div>

      {total === 0 ? (
        <div className="text-sm text-muted-foreground">{t('transcript.empty')}</div>
      ) : (
        <div className="space-y-3 text-sm">
          {visible.map((line, i) => (
            <div key={i} className="flex flex-col gap-0.5 sm:flex-row sm:gap-3">
              <div className="font-mono text-xs tabular-nums text-ink-3 sm:w-12 sm:shrink-0 sm:pt-0.5">
                {line.t}
              </div>
              <div className="min-w-0 flex-1">
                {/* §2: the assistant's lines get saved-deep attribution
                    (the one green with text contrast); the name itself is
                    the shape signal. Human speakers stay ink. */}
                <div
                  className={
                    'mb-0.5 text-xs font-semibold ' +
                    (line.speaker === 'Eric' ? 'text-saved-deep' : 'text-muted-foreground')
                  }
                >
                  {line.speaker}
                </div>
                <div className="text-foreground">{line.text}</div>
              </div>
            </div>
          ))}
          {!expanded && hidden > 0 ? (
            // sm:pl aligns with the transcript text column (3rem time
            // column + 0.75rem gap). The previous sm:pl-15 wasn't a real
            // Tailwind spacing step, so it silently applied nothing.
            <button
              type="button"
              onClick={onToggle}
              className="flex min-h-11 items-center text-[13px] text-muted-foreground transition-[color,opacity] active:opacity-70 [@media(hover:hover)_and_(pointer:fine)]:hover:text-foreground sm:pl-[3.75rem]"
            >
              {t('transcript.more', { count: hidden })}
            </button>
          ) : null}
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Recording shell (Phase 3 will wire R2-backed audio src)

function RecordingShell({ duration }: { duration: string | null }) {
  const { t } = useTranslation('calls')
  return (
    <div>
      <SectionLabel>{t('recording.header')}</SectionLabel>
      <div className="flex items-center gap-3 rounded-[10px] border border-border bg-muted px-3 py-2.5">
        <button
          type="button"
          aria-label={t('recording.play_label')}
          disabled
          className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-surface-2 text-ink-3 disabled:cursor-not-allowed"
        >
          <Play className="h-4 w-4" />
        </button>
        <div className="h-1.5 flex-1 rounded-full bg-border">
          {/* Playback progress is a human action → ink, not green. */}
          <div className="h-full w-0 rounded-full bg-foreground" />
        </div>
        {/* ink-2, not ink-3: §1.2's ink-3 exemption holds on white only —
            on the bg-muted box ink-3 computes 4.45:1, under the 4.5 floor. */}
        <div className="font-mono text-xs tabular-nums text-muted-foreground">
          0:00 / {duration ?? '—'}
        </div>
      </div>
      <div className="mt-1 text-[12px] text-ink-3">
        {t('recording.phase3_caption')}
      </div>
    </div>
  )
}

